import { useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { isHexDark } from '@/lib/color';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { localeTag } from '@/lib/localeTag';
import type { DeliveryPartner, RestaurantIdentity, RestaurantConfiguration, LogoVariantPreference } from '@/types';
import Marquee from './Marquee';
import { promotionMarqueeMessages } from '@/features/checkout/selectPromotion';
import { localizedMarquee } from '@/lib/localizeChrome';
import CartButton from '@/features/cart/CartButton';
import LanguageSwitcher from './LanguageSwitcher';
import Drawer from '@/components/ui/Drawer';
import MaskIcon from '@/components/ui/MaskIcon';
import { DeliverooIcon, UberEatsIcon } from '@/components/ui/DeliveryIcons';
import UserMascot from './UserMascot';
import UserAccountButton from './UserAccountButton';
import LoyaltyMenuLink from '@/features/loyalty/components/LoyaltyMenuLink';
import { useStyleMode } from '@/features/restaurant/useStyleMode';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { selectIsAuthenticated, selectUserInitial, selectCurrentUser } from '@/features/user/selectors';
import { selectIsInDineIn, selectTableSession } from '@/features/tableSession/tableSessionSlice';
import { openAccountModal } from '@/features/user/accountModalSlice';
import { useEffectiveOrderingState, type EffectiveOrderingState } from '@/features/restaurant/useEffectiveOrderingState';
import { isChannelPaused } from '@/features/restaurant/pausedChannels';
import { restaurantTz, currentClosingAt, todayIsoInTz } from '@/lib/hours';
import { dayLabelForOffset } from '@/features/checkout/dayLabel';
import { closureLabelIsSentence } from '@/features/restaurant/closureLabel';
import { useNow } from '@/lib/useNow';
import { useIsMobile } from '@/lib/useIsMobile';
import { useScrollAwayHeader } from './useScrollAwayHeader';

interface Props {
  identity: RestaurantIdentity;
  config: RestaurantConfiguration;
  onOpenTablePanel?: () => void;
}

export default function SiteHeader({ identity, config, onOpenTablePanel }: Props) {
  const { t, i18n } = useTranslation();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const partners = config.deliveryPartners ?? [];
  // Active promotions are advertised in the scrolling banner, ahead of the
  // merchant's own messages — derived so the banner always stays in sync.
  // SC-1081 — « active aujourd'hui » au jour civil du restaurant.
  // DF-0428 — both halves follow the customer's language, French as fallback.
  const marqueeMessages = [
    ...promotionMarqueeMessages(config.promotions ?? [], todayIsoInTz(restaurantTz(identity)), t),
    ...localizedMarquee(config.marqueeMessages, config.marqueeTranslations, i18n.language),
  ];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAuthed = useAppSelector(selectIsAuthenticated);
  const initial = useAppSelector(selectUserInitial);
  const currentUser = useAppSelector(selectCurrentUser);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // DF-0330 — the banner/badge below read the wall clock in the RESTAURANT's
  // own timezone, never the browser's.
  const tz = restaurantTz(identity);
  // Scan-to-order (dine-in) strips the header to the essentials: logo, language
  // and cart. No closed banner, status, Réserver, delivery partners, account or
  // marquee — the guest is seated and just needs to order.
  const isInDineIn = useAppSelector(selectIsInDineIn);
  const ordering = useEffectiveOrderingState(identity, isInDineIn, config.modes);
  const tableSession = useAppSelector(selectTableSession);
  const { pathname } = useLocation();
  const bannerVisible = identity.onlineOrderingFeatureEnabled && ordering.reason !== 'open' && !isInDineIn;
  const reservationOpen = config.acceptsReservations !== false && !isChannelPaused(identity, 'reservation');
  // In dine-in the logo must stay on the table-scoped URL — linking to the bare
  // /:restaurantUuid would drop the guest back into online ordering. The URL is
  // the source of truth for "on a table route" (RestaurantShell rehydrates the
  // session off it); the redux session can lag the URL — absent on a fresh
  // confirmation load in a new tab, during the pre-rehydrate frame, or after a
  // transient 401/403 current-poll clears it — so derive the target from the
  // path first, falling back to the session, then to the online home.
  const tableTokenInUrl = tableTokenFromPath(pathname, restaurantUuid);
  const homePath = tableTokenInUrl
    ? `/${restaurantUuid}/table/${tableTokenInUrl}`
    : isInDineIn && tableSession
      ? `/${restaurantUuid}/table/${tableSession.tableToken}`
      : `/${restaurantUuid}`;
  // SC-766: the marquee advertises promotions that never apply sur place
  // (selectPromotion only knows emporter / livraison), so it must be gone for
  // the WHOLE table journey. `isInDineIn` alone lags here too: a fresh scan
  // renders the covers picker BEFORE the join creates the session, so the
  // banner showed on that screen and only vanished once the menu loaded.
  const marqueeVisible = !isInDineIn && !tableTokenInUrl;
  const headerRef = useRef<HTMLElement>(null);
  // Opera premium trades the cartoon's thick 2px outlines for hairlines.
  const premium = useStyleMode() === 'premium';
  const edge = premium ? 'border' : 'border-2';
  // Mobile mounts a single, distinct top bar (one inline action row). Gating in
  // JS — not CSS — keeps exactly one set of action controls mounted, so the cart
  // registers the single fly-to-cart target and the desktop DOM stays unchanged.
  const isMobile = useIsMobile();
  const headerTop = useScrollAwayHeader(
    headerRef,
    isMobile && !isInDineIn && !mobileMenuOpen && pathname.replace(/\/$/, '') === `/${restaurantUuid}`,
  );
  // The cart lives in the header: while the header is scrolled away, float it
  // at the bottom so a guest who never scrolls back up can still check out
  // (shown only once the cart holds something — see CartButton 'float').
  const floatingCart = headerTop < 0 && ordering.canOrder;

  const logoSrc = resolveLogoSrc({
    logoUrl: identity.logoUrl,
    logoInverseUrl: identity.logoInverseUrl,
    preference: identity.theme.siteCommandeLogoVariant ?? 'auto',
    autoBackgroundIsDark: isHexDark(identity.theme.cardBackground),
  });

  const onAccountClick = () => {
    if (isAuthed) navigate(`/${restaurantUuid}/account`);
    else dispatch(openAccountModal({ mode: 'login' }));
  };
  return (
    <header ref={headerRef} style={{ top: headerTop }} className={`sticky top-0 z-[500] bg-paper transition-[top] duration-200 motion-reduce:transition-none ${premium ? 'border-b' : 'border-b-2'} border-[var(--border-color)]`}>
      {bannerVisible && (
        <div
          className="bg-[var(--header-bar-bg)] text-ink border-b-2 border-[var(--border-color)] font-mono text-xs sm:text-sm tracking-wide font-bold py-2 px-4 text-center"
          role="status"
        >
          {renderClosedBanner(ordering, t, i18n.language, tz, identity.country, reservationOpen)}
        </div>
      )}
      {isMobile ? (
        <MobileTopBar
          identity={identity}
          restaurantUuid={restaurantUuid ?? ''}
          homePath={homePath}
          logoSrc={logoSrc}
          premium={premium}
          edge={edge}
          isInDineIn={isInDineIn}
          canOrder={ordering.canOrder}
          reservationOpen={reservationOpen}
          isAuthed={isAuthed}
          initial={initial}
          currentUser={currentUser}
          onAccountClick={onAccountClick}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          tableNumber={tableSession?.tableNumber}
          onOpenTablePanel={onOpenTablePanel}
        />
      ) : (
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] sm:grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-4 px-4 py-3 max-w-[1440px] mx-auto">
        {/* LEFT slot — burger on mobile, brand on desktop */}
        <div className="justify-self-start flex items-center min-w-0 sm:justify-self-auto">
          {!isInDineIn && (
            <button
              type="button"
              data-fx-mute
              className={`sm:hidden inline-flex items-center justify-center h-11 w-11 bg-paper ${edge} border-ink rounded-btn-sm transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px]`}
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label={mobileMenuOpen ? t('header.menuClose') : t('header.menuOpen')}
              aria-expanded={mobileMenuOpen}
              aria-haspopup="dialog"
            >
              <BurgerIcon open={mobileMenuOpen} />
            </button>
          )}
          <Link
            to={homePath}
            className="hidden sm:flex items-center gap-3 min-w-0 overflow-hidden"
            title={identity.name}
          >
            <img src={logoSrc} alt={identity.name} className="h-12 shrink-0" />
            <span className="font-display text-ink text-2xl hidden lg:block truncate max-w-[280px] xl:max-w-[420px]">
              {identity.name}
            </span>
          </Link>
        </div>

        {/* CENTER slot — mobile logo, desktop status + Reserve + partners */}
        <div className="justify-self-stretch flex items-center justify-center sm:justify-self-stretch sm:gap-3 min-w-0 overflow-hidden">
          <Link
            to={homePath}
            className="sm:hidden flex items-center justify-center min-w-0 max-w-full"
            aria-label={identity.name}
          >
            <img src={logoSrc} alt={identity.name} className="max-h-12 w-auto max-w-full object-contain" />
          </Link>
          {isInDineIn && tableSession && onOpenTablePanel ? (
            <DineInTableButton tableNumber={tableSession.tableNumber} onClick={onOpenTablePanel} />
          ) : (
            <div className={`${isInDineIn ? 'hidden' : 'hidden sm:flex'} items-center gap-3 min-w-0`}>
              <StatusBadge state={ordering} hours={identity.hours} tz={tz} suppressDetail={bannerVisible} />
              {reservationOpen && (
                <Link
                  to={`/${restaurantUuid}/reservation`}
                  data-fx-mute
                  className={`inline-flex items-center gap-2 bg-paper ${edge} border-ink rounded-btn px-4 py-2 font-display text-ink no-underline shadow-stamp-sm transition-transform duration-200 hover:-translate-y-[2px] hover:shadow-stamp ${
                    premium ? 'uppercase tracking-[0.06em] text-[14px]' : 'hover:-translate-x-[2px]'
                  }`}
                  aria-label={t('header.reserveTable')}
                >
                  <MaskIcon src="/img/icon-calendar.svg" className="w-[18px] h-[18px]" />
                  <span>{t('header.reserve')}</span>
                </Link>
              )}
              {partners.length > 0 && (
                <div className="flex items-center gap-2 ps-3 border-s border-[var(--hairline)]">
                  {partners.map((p) => (
                    <DeliveryPartnerPill key={p.slug} partner={p} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT slot — language, mascot, cart */}
        <div className="justify-self-end flex items-center gap-2 sm:gap-3 shrink-0">
          <LanguageSwitcher className="hidden sm:block" />
          {!isInDineIn && (premium ? (
            <UserAccountButton
              mode={isAuthed ? 'authed' : 'guest'}
              initial={initial}
              onClick={onAccountClick}
              ariaLabel={isAuthed && currentUser ? t('header.myAccountNamed', { name: currentUser.name }) : t('header.myAccount')}
            />
          ) : (
            <UserMascot
              mode={isAuthed ? 'authed' : 'guest'}
              initial={initial}
              onClick={onAccountClick}
              ariaLabel={isAuthed && currentUser ? t('header.myAccountNamed', { name: currentUser.name }) : t('header.myAccount')}
            />
          ))}
          {(ordering.canOrder || isInDineIn) && (
            <CartButton restaurantUuid={restaurantUuid ?? ''} />
          )}
        </div>
      </div>
      )}
      {!isInDineIn && (
        <MobileMenuDrawer
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          restaurantUuid={restaurantUuid ?? ''}
          partners={partners}
          acceptsReservations={reservationOpen}
          isAuthed={isAuthed}
          onAccountClick={onAccountClick}
          phone={identity.phone}
        />
      )}
      {marqueeVisible && <Marquee messages={marqueeMessages} />}
      {/* Portaled out of the header: focusing a control inside it brings the
          header back (useScrollAwayHeader), which would unmount the pill
          between Android's tap-focus and the click. */}
      {floatingCart && createPortal(
        <div
          className="fixed inset-x-0 z-[400] flex justify-center pointer-events-none"
          style={{ bottom: 'max(1.25rem, calc(env(safe-area-inset-bottom) + 0.75rem))' }}
        >
          <div className="pointer-events-auto">
            <CartButton restaurantUuid={restaurantUuid ?? ''} variant="float" />
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}

// ──────────── Mobile top bar ────────────

// Compact action cell: bottom-aligned icon + tiny caption. The cluster reuses
// the restaurant's own icon set (mascot, shopping-bag, calendar, phone, globe).
// Fixed width (not min-width) so all five action cells are identical and the
// icons stay evenly spaced regardless of label length (e.g. RÉSERVER vs TEL).
const MOBILE_CELL = 'flex flex-col items-center justify-end gap-1 w-[42px] pt-1 text-ink no-underline';
const MOBILE_CELL_LABEL = 'text-[8px] leading-none font-mono uppercase tracking-[0.03em] text-ink/70 whitespace-nowrap';
const MOBILE_CELL_ICON = 'w-[25px] h-[25px]';

interface MobileTopBarProps {
  identity: RestaurantIdentity;
  restaurantUuid: string;
  homePath: string;
  logoSrc: string | undefined;
  premium: boolean;
  edge: string;
  isInDineIn: boolean;
  canOrder: boolean;
  reservationOpen: boolean;
  isAuthed: boolean;
  initial?: string;
  // Only `.name` is read; matches selectCurrentUser's `CurrentUser | undefined`.
  currentUser: { name: string } | undefined;
  onAccountClick: () => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (fn: (o: boolean) => boolean) => void;
  tableNumber?: string | number;
  onOpenTablePanel?: () => void;
}

function MobileTopBar({
  identity,
  restaurantUuid,
  homePath,
  logoSrc,
  premium,
  edge,
  isInDineIn,
  canOrder,
  reservationOpen,
  isAuthed,
  initial,
  currentUser,
  onAccountClick,
  mobileMenuOpen,
  setMobileMenuOpen,
  tableNumber,
  onOpenTablePanel,
}: MobileTopBarProps) {
  const { t } = useTranslation();
  const tel = telHref(identity.phone);
  const accountLabel = isAuthed && currentUser
    ? t('header.myAccountNamed', { name: currentUser.name })
    : t('header.myAccount');
  return (
    <div className="flex items-center gap-1.5 ps-3 pe-1 py-2 max-w-[1440px] mx-auto">
      {!isInDineIn && (
        <button
          type="button"
          data-fx-mute
          className={`shrink-0 inline-flex items-center justify-center h-10 w-10 bg-paper ${edge} border-ink rounded-btn-sm`}
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-label={mobileMenuOpen ? t('header.menuClose') : t('header.menuOpen')}
          aria-expanded={mobileMenuOpen}
          aria-haspopup="dialog"
        >
          <BurgerIcon open={mobileMenuOpen} />
        </button>
      )}

      <Link to={homePath} className="flex items-center min-w-0 flex-1" aria-label={identity.name}>
        <img src={logoSrc} alt={identity.name} className="max-h-8 w-auto max-w-[100px] object-contain" />
      </Link>

      {isInDineIn && tableNumber != null && onOpenTablePanel && (
        <DineInTableButton tableNumber={tableNumber} onClick={onOpenTablePanel} compact />
      )}

      {/* Plain container, not a nav landmark — the burger drawer below already
          provides the labelled main-nav; each action here is its own labelled control. */}
      <div className="flex items-end gap-1.5 shrink-0" data-testid="mobile-actions">
        {!isInDineIn && (
          <div className={`${MOBILE_CELL} relative`}>
            {premium ? (
              <UserAccountButton
                mode={isAuthed ? 'authed' : 'guest'}
                initial={initial}
                onClick={onAccountClick}
                ariaLabel={accountLabel}
                sizeClass="h-8 w-8"
              />
            ) : (
              <UserMascot
                mode={isAuthed ? 'authed' : 'guest'}
                initial={initial}
                onClick={onAccountClick}
                ariaLabel={accountLabel}
                sizeClass="h-8 w-8"
              />
            )}
            <span aria-hidden="true" className={MOBILE_CELL_LABEL}>{t('header.account')}</span>
          </div>
        )}

        {(canOrder || isInDineIn) && (
          <CartButton restaurantUuid={restaurantUuid} variant="cell" />
        )}

        {!isInDineIn && reservationOpen && (
          <Link
            to={`/${restaurantUuid}/reservation`}
            data-fx-mute
            className={MOBILE_CELL}
            aria-label={t('header.reserveTable')}
          >
            <MaskIcon src="/img/icon-calendar.svg" className={MOBILE_CELL_ICON} />
            <span className={MOBILE_CELL_LABEL}>{t('header.reserve')}</span>
          </Link>
        )}

        {!isInDineIn && tel && (
          <a
            href={tel}
            className={MOBILE_CELL}
            aria-label={t('header.callNamed', { phone: identity.phone ?? '' })}
          >
            <PhoneIcon className={MOBILE_CELL_ICON} />
            <span className={MOBILE_CELL_LABEL}>{t('header.phone')}</span>
          </a>
        )}

        <LanguageSwitcher variant="cell" />
      </div>
    </div>
  );
}

function DineInTableButton({
  tableNumber,
  onClick,
  compact = false,
}: {
  tableNumber: string | number;
  onClick: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const label = t('tablePanel.ariaLabel');

  return (
    <button
      type="button"
      data-testid="table-chip"
      data-fx-mute
      onClick={onClick}
      aria-label={`${label} · ${tableNumber}`}
      aria-haspopup="dialog"
      className={`group inline-flex shrink-0 cursor-pointer items-center gap-1.5 bg-mint border-2 border-ink rounded-btn shadow-stamp-sm transition-[transform,box-shadow] duration-150 hover:-translate-y-0.5 hover:-translate-x-0.5 hover:shadow-stamp active:translate-x-[2px] active:translate-y-[2px] active:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
        compact ? 'min-h-10 px-2 py-1' : 'min-h-11 px-3 py-1.5'
      }`}
    >
      <span className="flex min-w-0 flex-col items-start leading-none">
        <span className="font-mono text-[8px] font-bold uppercase tracking-[0.08em] text-ink/65">
          {label}
        </span>
        <span className="max-w-[72px] truncate font-display text-base text-ink">{tableNumber}</span>
      </span>
      <span
        aria-hidden="true"
        className="flex h-5 w-5 items-center justify-center rounded-full bg-ink text-paper transition-transform duration-150 group-hover:translate-x-0.5"
      >
        <svg
          viewBox="0 0 20 20"
          className="h-3.5 w-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m7 4 6 6-6 6" />
        </svg>
      </span>
    </button>
  );
}

// Extract the dine-in table token from a /:restaurantUuid/table/:tableToken[/…]
// path. Mirrors RestaurantShell's onTableRoute check so the header agrees with
// it on what counts as a table route. Returns null off a table route.
function tableTokenFromPath(pathname: string, restaurantUuid: string | undefined): string | null {
  if (!restaurantUuid) return null;
  const prefix = `/${restaurantUuid}/table/`;
  if (!pathname.startsWith(prefix)) return null;
  const token = pathname.slice(prefix.length).split('/')[0];
  return token || null;
}

function resolveLogoSrc({
  logoUrl,
  logoInverseUrl,
  preference,
  autoBackgroundIsDark,
}: {
  logoUrl: string | null;
  logoInverseUrl?: string | null;
  preference: LogoVariantPreference;
  autoBackgroundIsDark: boolean;
}) {
  const useInverse = preference === 'dark' || (preference === 'auto' && autoBackgroundIsDark);
  return useInverse
    ? (logoInverseUrl ?? logoUrl ?? undefined)
    : (logoUrl ?? logoInverseUrl ?? undefined);
}

// DF-0330 — these three formatters used to be hardcoded to Europe/Paris; the
// header now reads the wall clock in the RESTAURANT's own timezone (a DOM
// restaurant's `country` still reads "FR" while it runs 4-5h off Paris).
function timeFormatter(tz: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: tz,
  });
}

function weekdayLong(lng: string, tz: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(localeTag(lng), { weekday: 'long', timeZone: tz });
}

function dayFormatter(tz: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function dateLongFmt(lng: string): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(localeTag(lng), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatHHMM(d: Date, tz: string): string {
  // timeFormatter emits "HH:MM"; convert to "HHhMM" to match consumer SPA convention.
  return timeFormatter(tz).format(d).replace(':', 'h');
}

function formatDateLong(iso: string | undefined, lng: string): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  // Parse as UTC midnight so the displayed date is always the ISO calendar date
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return dateLongFmt(lng).format(d);
}

function telHref(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned ? `tel:${cleaned}` : undefined;
}

function renderClosedBanner(
  state: EffectiveOrderingState,
  t: TFunction,
  lng: string,
  tz: string,
  country?: string | null,
  reservationOpen = false,
): string {
  if (state.reason === 'operational-off') {
    // SC-1434: the shutter is per channel — only announce paused réservations
    // when the Réserver button next to this banner is gone too.
    return reservationOpen ? t('cart.orderingPaused') : t('header.banner.operationalOff');
  }
  if (state.reason === 'no-hours') {
    return t('header.banner.noHours');
  }
  if (state.reason === 'on-vacation') {
    const date = formatDateLong(state.closureUntil, lng);
    // DF-0566 — « Fermé – ouverture le 20 octobre » ne se glisse pas dans « Fermé pour … ».
    if (state.closureLabel && closureLabelIsSentence(state.closureLabel)) {
      return t('header.banner.onVacationOwnWords', { label: state.closureLabel, date });
    }
    // SC-1581 — un libellé vide (« ») vaut absence : « Fermé pour congés », pas « Fermé pour jusqu'au ».
    const label = state.closureLabel || t('header.banner.vacationDefaultLabel');
    return t('header.banner.onVacation', { label, date });
  }
  if (state.reason === 'preorder' && state.nextOpeningAt) {
    const time = formatHHMM(state.nextOpeningAt, tz);
    // DF-0314 / DF-0273 — une pré-commande du soir vise le lendemain : « pour
    // aujourd'hui » y serait faux, et « demain » nu ne dit rien à 23:50. Le
    // bandeau nomme le jour, dans le fuseau du restaurant.
    if (state.preorderNextDay) {
      return t('header.banner.preorderTomorrow', {
        day: dayLabelForOffset(new Date(), 1, lng, { timezone: tz, country }),
        time,
      });
    }
    return t('header.banner.preorder', { time });
  }
  if (state.reason === 'closed-now' && state.nextOpeningAt) {
    const now = new Date();
    const nx = state.nextOpeningAt;
    const dayFmt = dayFormatter(tz);
    const sameDay = dayFmt.format(now) === dayFmt.format(nx);
    const tomorrowDay = dayFmt.format(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    const tomorrow = tomorrowDay === dayFmt.format(nx);
    const time = formatHHMM(nx, tz);
    if (sameDay) return t('header.banner.reopenToday', { time });
    if (tomorrow) return t('header.banner.reopenTomorrow', { time });
    return t('header.banner.reopenWeekday', { weekday: weekdayLong(lng, tz).format(nx), time });
  }
  return t('header.banner.closed');
}

// ──────────── Status badge ────────────

interface StatusBadgeProps {
  state: EffectiveOrderingState;
  hours: RestaurantIdentity['hours'];
  tz: string;
  suppressDetail?: boolean;
}

function StatusBadge({ state, hours, tz, suppressDetail }: StatusBadgeProps) {
  const { t, i18n } = useTranslation();
  // 60-second tick so "ferme à HHhMM" stays accurate without remount.
  const now = useNow(60_000);
  const summary = describeStatus(state, hours, now, tz, t, i18n.language);
  return (
    <span
      className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-btn-sm border border-[var(--hairline)] bg-paper font-mono text-xs whitespace-nowrap"
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        // Status colors are semantic semaphores, not brand colors: a red-themed
        // restaurant that's open must still read green, never the theme hue.
        // Open also breathes a soft green glow (site-status-dot--live) to read as "live".
        className={`inline-block w-2 h-2 rounded-full${summary.openish ? ' site-status-dot--live' : ''}`}
        style={
          summary.openish
            ? { backgroundColor: '#00a368' }
            : { backgroundColor: '#ff3f62', boxShadow: '0 0 0 3px rgba(255,63,98,0.18)' }
        }
      />
      <span className="text-ink font-bold uppercase tracking-wide">{summary.label}</span>
      {summary.detail && !suppressDetail && (
        <span className="text-[var(--ink-soft)] normal-case">· {summary.detail}</span>
      )}
    </span>
  );
}

function describeStatus(
  state: EffectiveOrderingState,
  hours: RestaurantIdentity['hours'],
  now: Date,
  tz: string,
  t: TFunction,
  lng: string,
): { openish: boolean; label: string; detail?: string } {
  switch (state.reason) {
    case 'open': {
      const close = currentClosingAt(hours, now, tz);
      return {
        openish: true,
        label: t('header.status.open'),
        detail: close ? t('header.status.closesAt', { time: formatHHMM(close, tz) }) : undefined,
      };
    }
    case 'preorder':
      return {
        openish: true,
        label: t('header.status.preorder'),
        // DF-0314 — le soir, « ouvre à 11:30 » se lit comme dans une heure.
        detail: state.nextOpeningAt
          ? t(state.preorderNextDay ? 'header.status.tomorrowAt' : 'header.status.opensAt', {
              time: formatHHMM(state.nextOpeningAt, tz),
            })
          : undefined,
      };
    case 'closed-now': {
      if (!state.nextOpeningAt) return { openish: false, label: t('header.status.closed') };
      const dayFmt = dayFormatter(tz);
      const sameDay = dayFmt.format(now) === dayFmt.format(state.nextOpeningAt);
      const tomorrowDay = dayFmt.format(new Date(now.getTime() + 24 * 60 * 60 * 1000));
      const tomorrow = tomorrowDay === dayFmt.format(state.nextOpeningAt);
      const time = formatHHMM(state.nextOpeningAt, tz);
      if (sameDay) return { openish: false, label: t('header.status.closed'), detail: t('header.status.opensAt', { time }) };
      if (tomorrow) return { openish: false, label: t('header.status.closed'), detail: t('header.status.tomorrowAt', { time }) };
      return {
        openish: false,
        label: t('header.status.closed'),
        detail: t('header.status.weekdayAt', { weekday: weekdayLong(lng, tz).format(state.nextOpeningAt), time }),
      };
    }
    case 'on-vacation':
      // DF-0566 — le patron a donné la raison : « En congés » serait faux (travaux, avant ouverture…).
      if (!state.closureLabel) return { openish: false, label: t('header.status.onVacation') };
      return {
        openish: false,
        label: t('header.status.closed'),
        detail: closureLabelIsSentence(state.closureLabel) ? undefined : state.closureLabel,
      };
    case 'operational-off':
      return { openish: false, label: t('header.status.unavailable') };
    case 'no-hours':
    default:
      return { openish: false, label: t('header.status.closed') };
  }
}

// ──────────── Icons ────────────

function PhoneIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.69 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.33 1.85.56 2.81.69A2 2 0 0 1 22 16.92Z"/>
    </svg>
  );
}

// ──────────── Delivery partner pill ────────────

const PARTNER_STYLES: Record<string, { bg: string; logo: ReactNode }> = {
  'uber-eats': {
    bg: '#FFFFFF',
    logo: <UberEatsIcon className="h-7 w-auto text-ink" />,
  },
  deliveroo: {
    bg: '#00CCBC',
    logo: <DeliverooIcon className="h-7 w-auto text-paper" />,
  },
};

function DeliveryPartnerPill({ partner }: { partner: DeliveryPartner }) {
  const { t } = useTranslation();
  const style = PARTNER_STYLES[partner.slug];
  return (
    <a
      href={partner.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('header.orderOn', { name: partner.name })}
      title={t('header.orderOn', { name: partner.name })}
      className="inline-flex items-center justify-center h-10 px-2 rounded-btn-sm border-2 border-ink transition-transform duration-200 hover:-translate-y-[2px] hover:-translate-x-[2px] hover:shadow-stamp-sm"
      style={{ background: style?.bg ?? 'var(--paper)' }}
    >
      {style ? style.logo : (
        <span className="font-sans font-bold text-sm leading-none text-ink">{partner.name}</span>
      )}
    </a>
  );
}

// ──────────── Mobile drawer ────────────

interface MobileMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  restaurantUuid: string;
  partners: DeliveryPartner[];
  acceptsReservations?: boolean;
  isAuthed: boolean;
  onAccountClick: () => void;
  phone: string | null | undefined;
}

function MobileMenuDrawer({ open, onClose, restaurantUuid, partners, acceptsReservations, isAuthed, onAccountClick, phone }: MobileMenuDrawerProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const homePath = `/${restaurantUuid}`;
  const isHomeActive = location.pathname === homePath || location.pathname === `${homePath}/`;
  const tel = telHref(phone);

  const itemBase = 'block px-4 py-3 font-sans font-semibold text-base border-b border-ink/15';
  const itemActive = 'bg-ink text-paper';
  const itemIdle = 'text-ink hover:bg-cream';

  return (
    <Drawer open={open} onClose={onClose} side="left" width="320px" ariaLabel={t('header.menuLabel')}>
      <div className="px-4 py-3 border-b-2 border-ink flex items-center justify-between">
        <h2 className="font-display text-xl">{t('header.menuLabel')}</h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center justify-center w-9 h-9 border-2 border-ink rounded-btn-sm bg-paper font-display text-xl"
          aria-label={t('header.menuClose')}
        >
          ×
        </button>
      </div>
      <nav className="flex-1 overflow-y-auto" aria-label={t('header.mainNav')}>
        <Link
          to={homePath}
          onClick={onClose}
          className={`${itemBase} ${isHomeActive ? itemActive : itemIdle}`}
        >
          {t('header.home')}
        </Link>
        {acceptsReservations !== false && (
          <Link
            to={`/${restaurantUuid}/reservation`}
            onClick={onClose}
            className={`${itemBase} ${itemIdle}`}
          >
            {t('header.reservationNav')}
          </Link>
        )}
        {tel && (
          <a
            href={tel}
            onClick={onClose}
            className={`${itemBase} ${itemIdle} flex items-center gap-3`}
            aria-label={t('header.callNamed', { phone: phone ?? '' })}
          >
            <PhoneIcon />
            <span>{t('header.call')}</span>
          </a>
        )}

        <div className="h-2 bg-cream border-y border-ink/15" />

        <button
          type="button"
          onClick={() => { onClose(); onAccountClick(); }}
          className={`${itemBase} ${itemIdle} flex items-center gap-3 w-full text-left`}
        >
          <span
            aria-hidden="true"
            className="block w-5 h-5 bg-ink"
            style={{
              WebkitMaskImage: 'url(/img/icon-user.svg)',
              maskImage: 'url(/img/icon-user.svg)',
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
            }}
          />
          <span>{isAuthed ? t('header.myAccount') : t('header.login')}</span>
        </button>

        {isAuthed && (
          <LoyaltyMenuLink
            restaurantUuid={restaurantUuid}
            onNavigate={onClose}
            className={`${itemBase} ${itemIdle} flex items-center gap-3`}
          />
        )}

        <div className="px-4 pt-4 pb-2 border-t border-ink/15">
          <LanguageSwitcher variant="list" />
        </div>

        {partners.length > 0 && (
          <div className="px-4 pt-4 pb-2 flex flex-wrap items-center gap-2 border-t-2 border-ink mt-4">
            {partners.map((p) => (
              <DeliveryPartnerPill key={p.slug} partner={p} />
            ))}
          </div>
        )}
      </nav>
    </Drawer>
  );
}

function BurgerIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" aria-hidden="true">
      <path
        d={open ? 'M6 6 L18 18 M18 6 L6 18' : 'M4 7 H20 M4 12 H20 M4 17 H20'}
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
